import shortHash from 'short-hash';
import _ from 'lodash';
import ReactGA from 'react-ga4';

import {
  getGrowthbookFeatures,
  updateGrowthBookIndentifiers,
} from './global.experiments';
import { RootScopeEvents } from '../common/services/root-scope-types';
import config from './env.config';
import TrackingDispatcher from './tracking/_trackingDispatcher';

/**
 * @param {string} userId
 * @param {string} userHash
 */
function setUserId(userId, userHash) {
  if (Boolean(window.Intercom) && userHash) {
    window.Intercom('update', { user_id: userId, user_hash: userHash });
  }
}

/**
 *
 * @param {string} eventName
 * @param {Object} [additionalProperties]
 */
export function trackCustomEventGA4(eventName, additionalProperties) {
  ReactGA.event(eventName, {
    eventType: 'custom',
    ...additionalProperties,
  });
}

export function registerGoogleAnalytics() {
  const measurementId = config.googleAnalyticsSettings.measurementId;
  // register gtag (google analytics)
  window.dataLayer = Boolean(window.dataLayer) ? window.dataLayer : [];

  // eslint-disable-next-line unicorn/consistent-function-scoping
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  }
  gtag('js', new Date());
  gtag('config', measurementId);

  // setup GA4 tracking for custom events
  ReactGA.initialize([
    {
      trackingId: measurementId,
    },
  ]);
}

export default angular
  .module('doxy.global.tracking', [])
  .factory(
    'trackingFactory',
    function (
      /** @type {import("angular").IQService}*/ $q,
      /** @type {import("angular").ILogService} */ $log,
      /** @type {import("angular").IWindowService} */ $window,
      /** @type {import("angular").IRootScopeService} */ $rootScope,
      /** @type {import("angular").ITimeoutService} */ $timeout,
      /** @type {{ [s:string]: any; getSystemInfo: () => any; getPlatformData: () => any; }} */ Browser,
      /** @type {{ [s:string]: any; globalState: { type: string; }; }} */ pn,
      /** @type {{ [s:string]: any; activeProvider: { callSession: { id: any; }; channel: any; isInCall: any; peopleCollection: string | any[]; }; }} */ VideoProviderFactory,
      /** @type {{ [s:string]: any; getUserType: () => any; user?: { id: any; firstName?: any; lastName?: any; email?: any; speciality?: any; }; getPlanName: () => string; institution: { id: any; }; }} */ currentUserHelper,
      /** @type {{ [s:string]: any; getRoom: () => any; }} */ roomHelpersFactory,
      /** @type {{ [s:string]: any; getCurrentInstitution: () => any; }} */ institutionHelper,
      /** @type {string} */ NODE_ENV
    ) {
      if (NODE_ENV !== 'test') {
        getGrowthbookFeatures().catch((e) =>
          console.error(
            `Failure to fetch getGrowthbookFeaturesin trackingFactory: ${e}`
          )
        );
      }

      const globalTrackingDispatcher = new TrackingDispatcher(
        $q,
        $window,
        $log,
        Browser,
        currentUserHelper,
        institutionHelper,
        roomHelpersFactory,
        VideoProviderFactory
      );

      let fingerprint = '';

      $rootScope.$on(
        RootScopeEvents.CHECK_IN_SUCCESS,
        (
          /** @type {import("angular").IAngularEvent} */ e,
          /** @type {{ [s:string]: any; privateData: { ip: any; }; type: any; }} */ pnGlobalState
        ) => {
          fingerprint = shortHash(
            `${pnGlobalState.privateData.ip}-${
              navigator.userAgent
            }-${pn.getUserType()}`
          );
          $log.debug({ fingerprint });
        }
      );

      function _getUserType() {
        const userType = currentUserHelper.getUserType();
        return userType !== null ? userType : 'patient';
      }

      /**
       * @param {{ [s:string]: any; firstName?: any; lastName?: any; email?: any; }} user
       */
      function setUserProperties(user, skipSegment = false) {
        if (!skipSegment) {
          // segment.identify(user);
        }

        updateGrowthBookIndentifiers({ user });

        institutionHelper
          .getCurrentInstitution()
          .then((institution) => {
            updateGrowthBookIndentifiers({ institution });
            return undefined;
          })
          .catch((error) => console.error(error));
      }

      /**
       * @param {{ [s:string]: any; id: any; }} user
       */
      function trackSignIn(user) {
        setUserId(user.id, user.hash);
        setUserProperties(user, true);
      }

      const checkSegment = () => {
        if (
          !$window.analytics ||
          !$window.analytics.user ||
          !$window.analytics.user().anonymousId
        ) {
          console.info('Segment analytics not loaded');
          return false;
        }
        return true;
      };

      const segment = {
        getAnonymousId() {
          if (!checkSegment()) {
            return null;
          }
          return $window.analytics.user().anonymousId();
        },

        getUserType: () => _getUserType(),

        /**
         * @param {SegmentAnalytics.SegmentOpts} callback
         */
        identify(callback) {
          if (!checkSegment()) {
            return;
          }

          const { id, firstName, lastName, email, speciality } =
            currentUserHelper.user;

          $window.analytics.identify(
            id,
            {
              user_id: id,
              email,
              first_name: firstName,
              last_name: lastName,
              user_type: this.getUserType(),
              speciality,
            },
            {},
            callback
          );
        },
      };

      return {
        setUserId,
        trackSignIn,
        setUserProperties,
        global: globalTrackingDispatcher,
        segment,
      };
    }
  ).name;
